/**
 * V4-ZERNIO-ANALYTICS — Service frontend pour les analytics posts/channels/spaces.
 *
 * Tap dans les endpoints :
 *   GET  /api/v1/posts/{post_id}/analytics
 *   POST /api/v1/posts/{post_id}/analytics/refresh
 *   GET  /api/v1/channels/{channel_id}/analytics?days=30
 *   GET  /api/v1/spaces/{space_id}/analytics?days=30
 */
import { apiClient } from './apiClient';
import { Logger } from '../utils/logger';

export interface PostAnalyticsSnapshot {
  snapshot_date?: string | null;
  snapshot_at?: string | null;
  impressions: number;
  reach: number;
  views: number;
  likes: number;
  comments_count: number;
  shares: number;
  saves: number;
  clicks: number;
  engagement_rate: number;
}

export interface PostAnalyticsResponse {
  post_id: string;
  current: PostAnalyticsSnapshot;
  history: PostAnalyticsSnapshot[];
}

export interface ChannelAnalyticsResponse {
  channel_id: string;
  period_days: number;
  impressions: number;
  reach: number;
  likes: number;
  comments_count: number;
  shares: number;
  engagement_rate: number;
  post_count: number;
}

export interface NetworkBreakdown {
  network: string;
  impressions: number;
  reach: number;
  likes: number;
  engagement_rate: number;
  post_count: number;
}

export interface SpaceAnalyticsResponse {
  space_id: string;
  period_days: number;
  impressions: number;
  reach: number;
  likes: number;
  comments_count: number;
  engagement_rate: number;
  post_count: number;
  by_network: NetworkBreakdown[];
}

// V4-ANALYTICS-POSTS-HISTORY 2026-06-04 — contrat de la page Historique (port fidèle
// de la maquette Figma). Liste des posts publiés + dernier snapshot analytics + canaux ;
// le front calcule tout le panneau latéral (top tags/sources, activité, stats, variations).
export interface PostHistoryAnalytics {
  views: number;
  likes: number;
  comments_count: number;
  shares: number;
  impressions: number;
  reach: number;
}

export interface PostHistoryItem {
  id: string;
  channel_id: string;
  channel_network: string;
  published_date: string;
  content: string;
  image_url: string;
  source: string;
  tags: string[];
  analytics: PostHistoryAnalytics;
  // "available" | "unavailable" (source gated, ex. add-on Zernio) | "no_data" (jamais snapshotté).
  analytics_status?: string;
}

export interface ChannelHistoryMeta {
  id: string;
  network: string;
  custom_name: string;
  positioning: string;
  enabled: boolean;
  // V4-HISTORYTAB-CONNECTED-ONLY — true si le canal est réellement connecté à
  // Zernio (les coquilles vides non connectées sont masquées dans l'Historique).
  connected?: boolean;
}

export interface SpacePostsHistoryResponse {
  space_id: string;
  period_days: number;
  posts: PostHistoryItem[];
  channels: ChannelHistoryMeta[];
}

export const zernioAnalyticsService = {
  getPostAnalytics: async (postId: string, days = 30): Promise<PostAnalyticsResponse> => {
    Logger.debug('[ZernioAnalytics] Get post analytics', { postId, days });
    const response = await apiClient.get<PostAnalyticsResponse>(
      `/posts/${postId}/analytics`,
      { params: { days } },
    );
    return response.data;
  },

  refreshPostAnalytics: async (postId: string): Promise<PostAnalyticsSnapshot> => {
    Logger.info('[ZernioAnalytics] Refresh post analytics', { postId });
    const response = await apiClient.post<PostAnalyticsSnapshot>(
      `/posts/${postId}/analytics/refresh`,
    );
    return response.data;
  },

  getChannelAnalytics: async (channelId: string, days = 30): Promise<ChannelAnalyticsResponse> => {
    Logger.debug('[ZernioAnalytics] Get channel analytics', { channelId, days });
    const response = await apiClient.get<ChannelAnalyticsResponse>(
      `/channels/${channelId}/analytics`,
      { params: { days } },
    );
    return response.data;
  },

  getSpaceAnalytics: async (spaceId: string, days = 30): Promise<SpaceAnalyticsResponse> => {
    Logger.debug('[ZernioAnalytics] Get space analytics', { spaceId, days });
    const response = await apiClient.get<SpaceAnalyticsResponse>(
      `/spaces/${spaceId}/analytics`,
      { params: { days } },
    );
    return response.data;
  },

  // V4-ANALYTICS-POSTS-HISTORY — posts publiés + dernier snapshot + canaux (page Historique).
  getSpacePostsHistory: async (
    spaceId: string,
    opts: { days?: number } = {},
  ): Promise<SpacePostsHistoryResponse> => {
    const { days = 90 } = opts;
    Logger.debug('[ZernioAnalytics] Get space posts history', { spaceId, days });
    const response = await apiClient.get<SpacePostsHistoryResponse>(
      `/spaces/${spaceId}/analytics/posts-history`,
      { params: { days } },
    );
    return response.data;
  },
};
